/*global payhere*/
import React, { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { preapprovalApi } from "../../utils/api";
import TermsPopup from "../Subscription/payment/TermsPopup";

const PreapprovalButton = ({ firm }) => {
  const [showTerms, setShowTerms] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingPreapproval, setPendingPreapproval] = useState(null);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://www.payhere.lk/lib/payhere.js";
    script.defer = true;
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const initiatePreapproval = async () => {
    if (isProcessing) return;

    try {
      setIsProcessing(true);

      const orderId = `preapproval_${firm.slug}_${Date.now()}`;
      const amount = ((firm.subscription_amount || 5000) * (firm.num_subscriptions || 1)).toFixed(2);

      // Get hash from backend
      const preapprovalDetails = {
        firm_slug: firm.slug,
        order_id: orderId,
        amount: amount,
        currency: "LKR",
      };

      console.log("Generating recurring payment hash for:", preapprovalDetails);

      const { hash, merchant_id } = await preapprovalApi.start(preapprovalDetails);

      console.log("Recurring payment hash generated successfully");

      // Build recurring payment object
      const preapproval = {
        sandbox: import.meta.env.VITE_SANDBOX === "true",
        merchant_id: merchant_id,
        return_url: import.meta.env.VITE_CHAT_DOMAIN_URL || "https://chat.paralegal.lk",
        cancel_url: `${import.meta.env.VITE_MAIN_DOMAIN_URL}/authorize/${firm.slug}`,
        notify_url: import.meta.env.VITE_PREAPPROVAL_NOTIFY_URL,
        order_id: orderId,
        items: " Subscription Authorization",
        amount: amount,
        currency: "LKR",
        recurrence: firm.subscription_period || "1 Month",
        duration: "Forever",
        first_name: firm.name?.split(" ")[0] || "",
        last_name: firm.name?.split(" ").slice(1).join(" ") || "",
        email: firm.email || "",
        phone: "",
        address: "",
        city: "",
        country: "Sri Lanka",
        hash: hash,
        custom_1: firm.slug,
        custom_2: "chat_subscription",
      };

      // Store for use after terms acceptance
      setPendingPreapproval(preapproval);

      if (!acceptedTerms) {
        setShowTerms(true);
        return;
      }

      // Start PayHere preapproval
      startPayhere(preapproval);

    } catch (error) {
      console.error("Preapproval process error:", error);
      toast.error("Failed to initialize subscription. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const startPayhere = (preapproval) => {
    console.log("Starting PayHere recurring payment...");

    // Set up PayHere callbacks
    payhere.onCompleted = function onCompleted(orderId) {
      console.log("Recurring subscription completed. Order ID:", orderId);
      toast.success("Subscription activated! Redirecting...");
      // Redirect to chat after a short delay
      setTimeout(() => {
        window.location.href = import.meta.env.VITE_CHAT_DOMAIN_URL || "https://chat.paralegal.lk";
      }, 2000);
    };

    payhere.onDismissed = function onDismissed() {
      console.log("Recurring payment dismissed");
      toast.info("Subscription setup was cancelled.");
    };

    payhere.onError = function onError(error) {
      console.error("PayHere error:", error);
      toast.error("Payment error: " + error);
    };

    payhere.startPayment(preapproval);
  };

  // Handle terms acceptance
  useEffect(() => {
    if (acceptedTerms && pendingPreapproval) {
      startPayhere(pendingPreapproval);
      setPendingPreapproval(null);
    }
  }, [acceptedTerms, pendingPreapproval]);

  return (
    <>
      <ToastContainer position="bottom-right" autoClose={5000} />
      <button
        onClick={initiatePreapproval}
        disabled={isProcessing}
        className={`w-full text-white transition duration-500 ease-in-out rounded-25 text-18 px-30 h-50 ${
          isProcessing
            ? "bg-gray-400 cursor-not-allowed"
            : "bg-pl-accent hover:bg-pl-accent-hover"
        }`}
      >
        {isProcessing ? "Processing..." : "Subscribe Now"}
      </button>

      {showTerms && (
        <TermsPopup
          onAccept={() => {
            setAcceptedTerms(true);
            setShowTerms(false);
          }}
          onClose={() => {
            setShowTerms(false);
            setIsProcessing(false);
          }}
        />
      )}
    </>
  );
};

export default PreapprovalButton;
